import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiUrl } from '../utils/api';

export interface RigItem { id: string; name: string }
export interface StatusOverviewItem {
  label: string; // e.g., 'Бурение'
  color: string; // dot color
  stations: string[]; // list of station identifiers (names or numbers)
}

interface FiltersState {
  rigs: RigItem[];
  selectedStationId?: string;
  dateFrom: Date;
  dateTo: Date;
  statusOverview: StatusOverviewItem[];
}

interface FiltersContextValue extends FiltersState {
  setSelectedStationId: (id?: string) => void;
  setDateRange: (from: Date, to: Date) => void;
  setRigs: (list: RigItem[]) => void;
  setStatusOverview: (items: StatusOverviewItem[]) => void;
  refreshStations: () => Promise<void>;
  refreshStatusOverview: () => Promise<void>;
}

const FiltersContext = createContext<FiltersContextValue | undefined>(undefined);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const token = user?.access_token;

  const [rigs, setRigs] = useState<RigItem[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<Date>(() => new Date());
  const [dateTo, setDateTo] = useState<Date>(() => new Date());
  const [statusOverview, setStatusOverview] = useState<StatusOverviewItem[]>([
    { label: 'Бурение', color: '#32cd32', stations: [] },
    { label: 'Стоп', color: '#F39C12', stations: [] },
    { label: 'Не в сети', color: '#95A5A6', stations: [] },
    { label: 'Ошибка', color: '#E74C3C', stations: [] },
  ]);

  const setDateRange = (from: Date, to: Date) => { setDateFrom(from); setDateTo(to); };

  const refreshStations = async () => {
    if (!token) return;
    try {
      const url = apiUrl('/summary/static');
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) return; // silently skip
      const data = await res.json();
      const list = Array.isArray(data?.station_list) ? data.station_list : [];
      const newRigs: RigItem[] = list.map((s: any) => ({ id: String(s.id), name: String(s.name || s.id) }));
      setRigs(prev => {
        const equal = prev.length === newRigs.length && prev.every((p,i)=> p.id===newRigs[i].id && p.name===newRigs[i].name);
        return equal ? prev : newRigs;
      });
      setSelectedStationId(s => s ?? (newRigs[0]?.id));
      // Update status overview once rigs are known
      await refreshStatusOverviewInternal(newRigs);
    } catch {
      // ignore
    }
  };

  // Internal helper so we can pass an explicit rigs list and avoid stale state reads
  const refreshStatusOverviewInternal = async (_rigsList?: RigItem[]) => {
    if (!token) return;
    try {
      const url = apiUrl('/summary/dynamic');
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) return;
      const data = await res.json();
      const header = Array.isArray(data?.header) ? data.header : [];

      const drilling: string[] = [];
      const stopped: string[] = [];
      const offline: string[] = [];
      const error: string[] = [];

      header.forEach((h: any) => {
        const name = String(h?.name ?? '—');
        const st = String(h?.status ?? '').toLowerCase();
        if (!st) return;
        if (st.includes('drill')) drilling.push(name);
        else if (st.includes('stop') || st.includes('paused') || st.includes('maintenance')) stopped.push(name);
        else if (st.includes('offline') || st.includes('no signal')) offline.push(name);
        else if (st.includes('error') || st.includes('fault')) error.push(name);
      });

      setStatusOverview([
        { label: 'Бурение', color: '#32cd32', stations: drilling },
        { label: 'Стоп', color: '#F39C12', stations: stopped },
        { label: 'Не в сети', color: '#95A5A6', stations: offline },
        { label: 'Ошибка', color: '#E74C3C', stations: error },
      ]);
    } catch {
      // ignore
    }
  };

  const refreshStatusOverview = async () => refreshStatusOverviewInternal();

  useEffect(() => { refreshStations(); }, [token]);

  const value = useMemo<FiltersContextValue>(() => ({
    rigs,
    selectedStationId,
    dateFrom,
    dateTo,
    statusOverview,
    setSelectedStationId,
    setDateRange,
    setRigs,
    setStatusOverview,
    refreshStations,
    refreshStatusOverview,
  }), [rigs, selectedStationId, dateFrom, dateTo, statusOverview]);

  // Keep status overview in sync when rigs change
  useEffect(() => { refreshStatusOverviewInternal(); }, [token, rigs]);

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

export const useFilters = (): FiltersContextValue => {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error('useFilters must be used within FiltersProvider');
  return ctx;
};
